import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Linkedin, Instagram, Facebook, Youtube, Twitter } from "lucide-react";
import { PRODUCT_ORDER, PRODUCTS } from "../lib/products";
import { useTheme } from "../lib/theme";
import { analytics } from "../lib/analytics";

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_f69fe3c2-391a-4258-b806-47ff98915422/artifacts/gv4jpbei_logo_white_big.png";
const LOGO_BLACK = "https://customer-assets.emergentagent.com/job_f69fe3c2-391a-4258-b806-47ff98915422/artifacts/7bnwo8i7_logo_dark.png";

const Footer = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const year = new Date().getFullYear();
  const logo = theme === "dark" ? LOGO_WHITE : LOGO_BLACK;

  return (
    <footer className="relative mt-32 border-t border-foreground/10" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 md:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
          <div className="col-span-2 md:col-span-5">
            <img src={logo} alt="TRYB International" className="h-8 w-auto" />
            <p className="mt-5 text-sm text-muted-foreground max-w-sm leading-relaxed">
              {t("footer.description")}
            </p>
            <div className="mt-6 flex items-center gap-3 text-foreground/70">
              <a href="https://linkedin.com/company/trybbusiness" aria-label="LinkedIn" target="_blank" rel="noreferrer" className="hover:text-foreground transition" data-testid="footer-linkedin"><Linkedin className="h-4 w-4" /></a>
              <a href="https://instagram.com/trybbusiness" aria-label="Instagram" target="_blank" rel="noreferrer" className="hover:text-foreground transition" data-testid="footer-instagram"><Instagram className="h-4 w-4" /></a>
              <a href="https://facebook.com/trybbusiness" aria-label="Facebook" target="_blank" rel="noreferrer" className="hover:text-foreground transition" data-testid="footer-facebook"><Facebook className="h-4 w-4" /></a>
              <a href="https://twitter.com/trybofficial" aria-label="Twitter" target="_blank" rel="noreferrer" className="hover:text-foreground transition" data-testid="footer-twitter"><Twitter className="h-4 w-4" /></a>
              <a href="https://youtube.com/@trybofficial" aria-label="YouTube" target="_blank" rel="noreferrer" className="hover:text-foreground transition" data-testid="footer-youtube"><Youtube className="h-4 w-4" /></a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {t("footer.product")}
            </p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {PRODUCT_ORDER.map((slug) => (
                <li key={slug}>
                  <Link to={`/${slug}`} className="text-foreground/80 hover:text-foreground transition" data-testid={`footer-product-${slug}`}>
                    {PRODUCTS[slug].name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {t("footer.company")}
            </p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/#about" className="text-foreground/80 hover:text-foreground transition">{t("footer.about")}</Link></li>
              <li><Link to="/#contact" className="text-foreground/80 hover:text-foreground transition">{t("footer.contact")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {t("footer.legal")}
            </p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><a href="https://trybloyalty.com/tpost/464tznl5k1-privacy-policy" target="_blank" rel="noreferrer" className="text-foreground/80 hover:text-foreground transition">{t("footer.privacy")}</a></li>
              <li><a href="https://trybloyalty.com/tpost/bvooo2ct21-terms-of-use" target="_blank" rel="noreferrer" className="text-foreground/80 hover:text-foreground transition">{t("footer.terms")}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-foreground/10 flex flex-col gap-3 text-xs text-muted-foreground">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p>© 2022—{year} TRYB. {t("footer.rights")} · {t("footer.legalEntity")}</p>
            <p className="tracking-wider uppercase text-[10px]">tryb.business · USA · Morocco · Spain · Turkey</p>
          </div>
          <p className="text-center md:text-left text-[11px]" data-testid="footer-credit">
            Made with <span aria-hidden="true">❤️</span><span className="sr-only">love</span> and <span aria-hidden="true">☕</span><span className="sr-only">coffee</span> by{" "}
            <a
              href="https://crescents.ma"
              target="_blank"
              rel="noopener noreferrer"
              title="Agence Web Tanger"
              aria-label="Agence Web Tanger — CRESCENTS"
              onClick={() => analytics.outboundClick("https://crescents.ma", "crescents_footer")}
              className="tryb-link font-semibold text-foreground/80 hover:text-foreground transition"
              data-testid="footer-credit-crescents"
            >
              CRESCENTS
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
